import { z } from "zod";

import {
  CustomFieldRecord,
  CustomFieldType,
} from "@/shared/domain/customFields";

/** Max length for String custom-field values (matches the backend constraint). */
const MAX_STRING_VALUE_LENGTH = 256;

/**
 * Zod schema validating a single value against its custom field's type — the
 * source of truth for whether a value is valid to save. Inputs already emit
 * wire-shaped values (string / number / boolean / ISO string), so the schema
 * validates without transforming.
 */
export function valueSchemaForField(field: CustomFieldRecord): z.ZodTypeAny {
  switch (field.field_type) {
    case CustomFieldType.Number:
      // Rejects NaN/Infinity (not JSON-encodable) and non-numbers (null, bool).
      return z.number();
    case CustomFieldType.Boolean:
      return z.boolean();
    case CustomFieldType.Timestamp:
      // Rejects non-ISO timestamps or ISO timestamps without a timezone.
      return z.iso.datetime({ offset: true });
    case CustomFieldType.Enum: {
      const options = field.options?.enum_values ?? [];
      return z.string().refine((value) => options.includes(value));
    }
    case CustomFieldType.String:
    default:
      return z.string().trim().min(1).max(MAX_STRING_VALUE_LENGTH);
  }
}

/**
 * Whether a draft value is valid to commit for its field. Powers the confirm
 * (✓) button so a half-typed number, a blank string, or an unselected enum
 * can't be saved. Clearing a value has its own explicit UI control.
 */
export function isCommittableValue(
  field: CustomFieldRecord,
  value: unknown,
): boolean {
  return valueSchemaForField(field).safeParse(value).success;
}
